import { FC } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useCapacityManagement } from 'src/apis/capacity-management/use-capacity-management';
import { GroupWrap } from 'src/components/group-wrap';
import { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useTrFlags } from 'src/hooks/use-tr-flags';
import {
  isRemitReportingSubscriptionInfoLoaded,
  useRemitReportingSubscriptionInfo,
} from 'src/pages/reporting/secondary-tradings/overview/use-remit-reporting-subscription-info';

const routes = ['/reporting'];
const angularRoutes = ['#/reporting'];

export const isReportingActive = (pathname: string, hash: string) =>
  routes.some(
    (route) =>
      pathname.startsWith(route) ||
      (pathname.startsWith('/platform') &&
        angularRoutes.some((angularRoute) => hash.startsWith(angularRoute)))
  );

export const SecondaryNavReporting: FC<{
  activeSection: ActiveSection;
}> = ({ activeSection }) => {
  const { pathname, hash } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { minTablet } = useBreakpoints();
  const capacityManagement = useCapacityManagement();
  const subscriptionInfoRequest = useRemitReportingSubscriptionInfo('PRIMARY');

  const trFlags = useTrFlags();
  const isTrSection = useRouteMatch('/reporting/tr');

  if (!activeSection.reporting) return null;

  return (
    <GroupWrap
      gap={2}
      data-testid="secondary-nav"
      alignItems={!minTablet ? 'stretch' : 'center'}
      flexDirection={!minTablet ? 'column' : 'row'}
    >
      {monolithUser?.isShipper && capacityManagement?._links && (
        <Link mode="secondary" to="/reporting/my-transactions">
          My Transactions
        </Link>
      )}

      {monolithUser?.isTso && (
        <Link
          mode="secondary"
          to="/reporting/products/uploaded"
          isActive={pathname.startsWith('/reporting/products/')}
        >
          Products
        </Link>
      )}

      <Link mode="secondary" to="/reporting/standard">
        Standard Reports
      </Link>

      <Link mode="secondary" to="/reporting/auctions">
        Auctions
      </Link>

      <Link
        mode="secondary"
        to="/reporting/secondary"
        isActive={
          pathname.startsWith('/reporting/secondary') ||
          (pathname.startsWith('/platform') &&
            hash.startsWith('#/reporting/secondary'))
        }
      >
        Secondary Tradings
      </Link>

      {monolithUser?.isTso && capacityManagement?._links && (
        <Link mode="secondary" to="/reporting/shipper-transactions">
          Shipper Transactions
        </Link>
      )}

      {monolithUser?.isTso &&
        Boolean(
          capacityManagement?._links?.monthlyStatements ||
            capacityManagement?._links?.monthlyStatementsWithCommodityFeeSupport
        ) && (
          <Link mode="secondary" to="/reporting/monthly-statements">
            Monthly Statements
          </Link>
        )}

      {monolithUser?.isTso &&
        Boolean(capacityManagement?._links?.balancingGroupAllocations) && (
          <Link mode="secondary" to="/reporting/balancing-group-allocations">
            Balancing Group Allocations
          </Link>
        )}

      {(trFlags.userCanAcces || isTrSection) && (
        <Link mode="secondary" to="/reporting/tr">
          Transparency Reporting
        </Link>
      )}

      {isRemitReportingSubscriptionInfoLoaded(subscriptionInfoRequest) && (
        <Link mode="secondary" to="/reporting/remit-reports">
          REMIT Reports
        </Link>
      )}

      {monolithUser?.isShipper && (
        <>
          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/reporting/capacity-linking`}
          >
            Linked Capacity
          </ExternalLink>

          <Link
            mode="secondary"
            to="/reporting/fcfs"
            isActive={
              pathname.startsWith('/reporting/fcfs') ||
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/reporting/fcfs/details'))
            }
          >
            FCFS Bookings
          </Link>

          <Link
            mode="secondary"
            to="/reporting/surrenders"
            isActive={
              pathname.startsWith('/reporting/surrenders') ||
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/reporting/surrender/details'))
            }
          >
            Surrenders
          </Link>

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/reporting/contracts`}
          >
            Contract Positions
          </ExternalLink>

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/reporting/new`}
          >
            Report Wizard
          </ExternalLink>
        </>
      )}
    </GroupWrap>
  );
};
